import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Pause } from 'lucide-react';

interface AudioVisualizerProps {
  isRecording: boolean;
  stream: MediaStream | null;
  onStopRecording: () => void;
  onPauseRecording?: () => void;
  elapsedLabel?: string;
}

const BAR_COUNT = 8;

const AudioVisualizer: React.FC<AudioVisualizerProps> = ({ isRecording, stream, onStopRecording, onPauseRecording, elapsedLabel }) => {
  const barsRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>(0);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const qualityMetricsRef = useRef<{ rms: number[]; peak: number[]; dc: number[] }>({
    rms: [],
    peak: [],
    dc: [],
  });

  const qualityStartTimeRef = useRef<number | null>(null);
  const qualityResetTimeRef = useRef<number | null>(null);
  const [qualityStatus, setQualityStatus] = useState<false | 'silent' | 'low'>(false);
  const [showPersistentQualityAlert, setShowPersistentQualityAlert] = useState(false);
  const alarmPlayedRef = useRef<boolean>(false);

// Keep latest quality status in a ref to avoid stale closures in rAF loop
const qualityStatusRef = useRef<false | 'silent' | 'low'>(false);
const setQuality = useCallback((status: false | 'silent' | 'low') => {
  qualityStatusRef.current = status;
  setQualityStatus(status);
}, []);

  // Function to play alarm sound
  const playAlarmSound = useCallback(() => {
    try {
      // Create a simple alarm sound using Web Audio API
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Create a beeping alarm sound
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.2);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.3);
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.4);
    } catch (error) {
      console.warn('Could not play alarm sound:', error);
    }
  }, []);

  // Effect to play alarm when quality status becomes 'silent'
  useEffect(() => {
    if (qualityStatus === 'silent' && !alarmPlayedRef.current) {
      playAlarmSound();
      alarmPlayedRef.current = true;
    } else if (qualityStatus !== 'silent') {
      alarmPlayedRef.current = false;
    }
  }, [qualityStatus, playAlarmSound]);

  const draw = useCallback(() => {
    if (!isRecording || !analyserRef.current || !dataArrayRef.current) return;

    animationRef.current = requestAnimationFrame(draw);

    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;

    analyser.getByteFrequencyData(dataArray);

    // Update wave-bar scales via CSS custom property
    const container = barsRef.current;
    if (container) {
      const bars = container.children;
      for (let i = 0; i < bars.length; i++) {
        const dataIndex = Math.floor((i / BAR_COUNT) * dataArray.length);
        const intensity = dataArray[dataIndex] / 255;
        const scale = 0.15 + intensity * 0.85;
        (bars[i] as HTMLElement).style.setProperty('--scale', String(scale));
      }
    }

    // === Audio Quality Metrics ===
    const floatArray = new Float32Array(analyser.fftSize);
    analyser.getFloatTimeDomainData(floatArray);

    const rms = Math.sqrt(floatArray.reduce((sum, x) => sum + x * x, 0) / floatArray.length);
    let peak = 0;
    let sum = 0;
    for (let i = 0; i < floatArray.length; i++) {
      const val = floatArray[i];
      const absVal = Math.abs(val);
      if (absVal > peak) peak = absVal;
      sum += val;
    }
    const mean = sum / floatArray.length;
    const dcOffset = Math.abs(mean);

    // Update sliding window (last ~2s worth of data)
    const q = qualityMetricsRef.current;
    q.rms.push(rms);
    q.peak.push(peak);
    q.dc.push(dcOffset);

    const maxWindow = 20;
    if (q.rms.length > maxWindow) {
      q.rms.shift();
      q.peak.shift();
      q.dc.shift();
    }

    // Evaluate overall quality
    const avgRMS = q.rms.reduce((a, b) => a + b, 0) / q.rms.length;
    const avgPeak = q.peak.reduce((a, b) => a + b, 0) / q.peak.length;
    const avgDC = q.dc.reduce((a, b) => a + b, 0) / q.dc.length;

    const isSilent = avgRMS < 0.005;
    const isLowQuality = !isSilent && (avgRMS < 0.01 || avgPeak > 0.98 || avgDC > 0.05);
    const currentStatus: typeof qualityStatus = isSilent ? 'silent' : isLowQuality ? 'low' : false;

    const now = performance.now();

    if (currentStatus) {
      if (qualityStartTimeRef.current === null) {
        qualityStartTimeRef.current = now;
      }

      const elapsed = now - qualityStartTimeRef.current;
      if (elapsed >= 5000 && qualityStatusRef.current !== currentStatus) {
        setQuality(currentStatus);
      }
      // A full minute of unusable signal escalates from a hint to a standing
      // alert: the recording keeps running, so the user must be told loudly.
      if (elapsed >= 60000) {
        setShowPersistentQualityAlert(true);
      }

      // reset cooldown tracking
      qualityResetTimeRef.current = null;

    } else {
      // signal is currently good
      if (qualityStatusRef.current !== false) {
        if (qualityResetTimeRef.current === null) {
          qualityResetTimeRef.current = now;
        } else if (now - qualityResetTimeRef.current >= 2000) {
          // good for 2s → reset warning
          setQuality(false);
          setShowPersistentQualityAlert(false);
          qualityStartTimeRef.current = null;
          qualityResetTimeRef.current = null;
        }
      } else {
        // nothing active — clear any prior bad-timer
        qualityStartTimeRef.current = null;
        qualityResetTimeRef.current = null;
      }
    }
    
  }, [isRecording, setQuality]);

  useEffect(() => {
    if (!isRecording || !stream) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      // Tear the graph down rather than just stopping the loop: an open
      // AudioContext still attached to the microphone can keep the browser's
      // recording indicator lit after the tracks are stopped.
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      analyserRef.current = null;
      dataArrayRef.current = null;
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(() => undefined);
        audioContextRef.current = null;
      }
      setQuality(false);
      setShowPersistentQualityAlert(false);
      qualityStartTimeRef.current = null;
      qualityResetTimeRef.current = null;
      return;
    }

    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
    }

    const audioContext = audioContextRef.current;

    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }

    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();

    analyser.fftSize = 256;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    source.connect(analyser);
    analyserRef.current = analyser;
    dataArrayRef.current = dataArray;
    sourceRef.current = source;

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
    };
  }, [isRecording, stream, draw]);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(() => undefined);
        audioContextRef.current = null;
      }
    };
  }, []);

  if (!isRecording) {
    return null;
  }

  return (
    <div className="relative flex flex-col items-center">
      {showPersistentQualityAlert && (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-0 bg-brand-red/20 transition-colors duration-500 pointer-events-none"
        />
      )}

      <div className="relative z-10 flex flex-col items-center">
        <span className="badge-context mb-4">Actieve opname</span>

        {elapsedLabel && (
          <p
            className="mb-4 text-3xl font-semibold tabular-nums text-brand-blauw"
            aria-label="Opnameduur"
            role="timer"
          >
            {elapsedLabel}
          </p>
        )}

        <div
          ref={barsRef}
          className={`flex items-end justify-center gap-2 h-32 ${qualityStatus === 'silent' ? 'animate-pulse' : ''}`}
          aria-hidden="true"
        >
          {Array.from({ length: BAR_COUNT }, (_, i) => (
            <div
              key={i}
              className="wave-bar w-3 h-full rounded-full"
              style={{
                '--scale': '0.15',
                backgroundColor: i % 2 === 0 ? '#2380AC' : '#E73459',
              } as React.CSSProperties}
            />
          ))}
        </div>

        {/* The transient hints give way to the standing alert, so the user is
            never shown two competing warnings at once. */}
        {qualityStatus === 'silent' && !showPersistentQualityAlert && (
          <div className="mt-4 text-red-600 text-sm text-center max-w-sm animate-pulse">
            <div className="flex items-center justify-center space-x-2">
              <span className="text-red-500">⚠️</span>
              <span>Geen geluid gedetecteerd — is je microfoon uitgeschakeld of te ver weg?</span>
              <span className="text-red-500">⚠️</span>
            </div>
          </div>
        )}

        {showPersistentQualityAlert && (
          <div
            role="alert"
            aria-live="assertive"
            className="mt-4 max-w-md rounded-lg border-2 border-red-700 bg-red-900 px-4 py-3 text-center text-sm font-semibold text-white shadow-lg"
          >
            Al langer dan een minuut wordt geen bruikbaar microfoonsignaal gedetecteerd. De opname loopt door, maar controleer de microfoon voordat je verdergaat.
          </div>
        )}

        {qualityStatus === 'low' && !showPersistentQualityAlert && (
          <div className="mt-4 text-red-600 text-sm text-center max-w-sm">
            Audio kwaliteit is slecht — het kan clippen, te stil of vervormd zijn.
          </div>
        )}

        <div className="pt-6 flex flex-wrap items-center justify-center gap-4">
          {onPauseRecording && (
            <button
              type="button"
              onClick={onPauseRecording}
              className="btn-gesprekskompas-primary sm:w-auto"
            >
              <Pause className="w-5 h-5" aria-hidden="true" />
              Pauzeer opname
            </button>
          )}

          <button
            type="button"
            onClick={onStopRecording}
            className="btn-roll-effect cursor-pointer"
          >
            <span className="btn-accent w-full lg:w-auto rounded-xl text-xl lg:px-5 h-12">
              Verstuur om te verwerken
            </span>
            <span className="btn-roll-circle bg-brand-red" aria-hidden="true">
              <img src={`${process.env.PUBLIC_URL}/assets/arrow.svg`} alt="" className="w-6" />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AudioVisualizer;